import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  InputAdornment,
  TextField,
  Pagination,
  Modal,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Search,
  Edit,
  CheckCircleOutline,
  HourglassEmpty,
  CancelOutlined,
  Info,
  Percent,
  Close,
} from "@mui/icons-material";
import { RealtorCollaborationRequest } from "../../../types/realtorCollaborationRequest";
import {
  acceptCollaborationRequest,
  getByPropertyId,
  rejectCollaborationRequest,
  updateCollaborationRequest,
} from "../../../services/realtorCollaborationRequestsService";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { Stack } from "@mui/system";
import { useNavigate } from "react-router/dist";
import { ProfileDto } from "../../../types/UserToken";
import {
  Autocomplete,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import { Add, Person } from "@mui/icons-material";
import { UserService } from "../../../services/authService";
import { createCollaborationRequest } from "../../../services/realtorCollaborationRequestsService";
import { useAuth } from "../../../context/AuthContext";
import { fetchAllRealtors } from "../../../services/realtorService";

const getStatusChip = (status: number) => {
  switch (status) {
    case 0:
      return (
        <Chip
          label="Pendiente"
          sx={{ backgroundColor: "#ffa726", color: "white" }}
        />
      );
    case 1:
      return (
        <Chip
          label="Aceptada"
          sx={{ backgroundColor: "#66bb6a", color: "white" }}
        />
      );
    case 2:
      return (
        <Chip
          label="Rechazada"
          sx={{ backgroundColor: "#ef5350", color: "white" }}
        />
      );
    default:
      return <Chip label="Desconocido" />;
  }
};

const COLORS = [
  "#4caf50",
  "#2196f3",
  "#ff9800",
  "#9c27b0",
  "#e91e63",
  "#00bcd4",
];

interface Broker {
  id: number;
  userProfileId: number;
}

export const CollaborationRequestsSection = ({
  selectedProperty,
}: {
  selectedProperty: any;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { userToken } = useAuth();
  const [selectedAction, setSelectedAction] = useState<
    "accept" | "reject" | null
  >(null);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRequest, setDetailsRequest] = useState<any | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<RealtorCollaborationRequest[]>([]);
  const [snackBarOpen, setSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState(false);
  const [message, setMessage] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editRequest, setEditRequest] = useState<any | null>(null);
  const [editCommission, setEditCommission] = useState<string>("");
  const [editIsPaid, setEditIsPaid] = useState<boolean>(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [addBrokerOpen, setAddBrokerOpen] = useState(false);
  const [searchUsers, setSearchUsers] = useState<ProfileDto[]>([]);
  const [selectedBroker, setSelectedBroker] = useState<ProfileDto | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [addBrokerForm, setAddBrokerForm] = useState({
    requestName: "",
    description: "",
    commissionPercentaje: "",
  });
  const [savingAddBroker, setSavingAddBroker] = useState(false);
  const navigate = useNavigate();

  const itemsPerPage = 5;

  useEffect(() => {
    const fetchRealtors = async () => {
      const response = await fetchAllRealtors();
      console.log(response);
      setBrokers(response);
    };

    fetchRealtors();
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getByPropertyId(selectedProperty.id_Property);
      if (response.success) {
        setRequests(response.data);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    }
    setLoading(false);
  }, [selectedProperty.id_Property]);

  useEffect(() => {
    setTimeout(() => {
      if (selectedProperty.id_Property) {
        fetchRequests();
      }
    }, 1500);
  }, [fetchRequests, selectedProperty.id_Property]);

  const acceptRequests = async () => {
    try {
      const response = await acceptCollaborationRequest(
        selectedRequestId as number
      );
      if (response?.success) {
        await fetchRequests();
        setMessage("La solicitud ha sido aceptada correctamente.");
        setSnackbarOpen(true);
      } else {
        setSnackbarError(true);
        setMessage(response?.message || "Error al aceptar la solicitud");
      }
    } catch (error: any) {
      console.error("Error fetching accepted requests:", error);
      setSnackbarError(true);
      setMessage(error.message || "Error al aceptar la solicitud");
    }
  };

  const rejectRequests = async () => {
    try {
      const response = await rejectCollaborationRequest(
        selectedRequestId as number
      );
      if (response.success) {
        await fetchRequests();
        setMessage("La solicitud ha sido rechazada correctamente.");
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error("Error fetching rejected requests:", error);
      setSnackbarError(true);
      setMessage(error.message || "Error al rechazar la solicitud");
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleAction = (
    action: "accept" | "reject",
    id: number,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setSelectedAction(action);
    setSelectedRequestId(id);
    setDialogOpen(true);
  };

  const confirmAction = () => {
    if (selectedAction === "accept") {
      acceptRequests();
    } else if (selectedAction === "reject") {
      rejectRequests();
    }
    setDialogOpen(false);
  };

  const handleOpenDetails = (req: any) => {
    setDetailsRequest(req);
    setDetailsOpen(true);
  };

  const filteredRequests = requests.filter(
    (req) =>
      req.requestName?.toLowerCase().includes(search.toLowerCase()) ||
      req.contact?.fullName?.toLowerCase().includes(search.toLowerCase()) ||
      req.description?.toLowerCase().includes(search.toLowerCase()) ||
      req.propertyListing?.title
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      req.propertyListing?.address_Description
        ?.toLowerCase()
        .includes(search.toLowerCase())
  );

  const paginatedRequests = filteredRequests.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const acceptedRequests = requests.filter((r) => r.status === 1);

  const totalShared = acceptedRequests.reduce(
    (sum, r) => sum + r.commissionPercentaje,
    0
  );

  const pieData = [
    {
      name: "Tu comisión",
      value: 100 - totalShared,
    },
    ...acceptedRequests.map((r, index) => ({
      name: r.contact?.fullName || `Broker ${index + 1}`,
      value: r.commissionPercentaje,
    })),
  ];

  const handleEditOpen = (req: any) => {
    setEditRequest(req);
    setEditCommission(req.commissionPercentaje);
    setEditIsPaid(req.isPaid);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editRequest) return;

    setSavingEdit(true);
    try {
      const response = await updateCollaborationRequest(editRequest.id, {
        commissionPercentaje: parseFloat(editCommission),
        isPaid: editIsPaid,
      });

      if (response.success) {
        await fetchRequests();

        setMessage(response.message || "Solicitud actualizada correctamente.");
        setSnackbarOpen(true);
        setEditOpen(false);
      }
    } catch (error: any) {
      setMessage(error?.message || "Error al guardar los cambios.");
      setSnackbarError(true);
    } finally {
      setSavingEdit(false);
    }
  };

  const hasChanges = () =>
    editRequest &&
    (editCommission !== editRequest.commissionPercentaje ||
      editIsPaid !== editRequest.isPaid);

  const getStatusCardHeader = (status: number) => {
    switch (status) {
      case 0: // Pendiente
        return {
          label: "Pendiente",
          color: "#ffa726",
          icon: <HourglassEmpty sx={{ mr: 1 }} />,
        };
      case 1: // Aceptada
        return {
          label: "Aceptada",
          color: "#66bb6a",
          icon: <CheckCircleOutline sx={{ mr: 1 }} />,
        };
      case 2: // Rechazada
        return {
          label: "Rechazada",
          color: "#ef5350",
          icon: <CancelOutlined sx={{ mr: 1 }} />,
        };
      default:
        return {
          label: "Desconocido",
          color: "#bdbdbd",
          icon: null,
        };
    }
  };

  // Función para obtener las opciones de comisión disponibles
  const getAvailableCommissionOptions = (commission: string | null) => {
    if (!commission) {
      return [50, 40, 20]; // Opciones por defecto si no hay términos especificados
    }

    const commissionText = commission.toLowerCase().trim();
    const percentageRegex = /(\d+)%?/g;
    const matches = commissionText.match(percentageRegex);

    if (matches) {
      const percentages = matches
        .map((match) => parseInt(match.replace("%", "")))
        .filter((num) => num > 0 && num <= 100)
        .sort((a, b) => b - a); // Ordenar de mayor a menor

      // Remover duplicados
      const uniquePercentages = [...new Set(percentages)];

      if (uniquePercentages.length > 0) {
        return uniquePercentages;
      }
    }

    // Si no se pueden extraer porcentajes válidos, usar opciones por defecto
    return [50, 40, 20];
  };

  const commissionOptions = getAvailableCommissionOptions(
    selectedProperty?.commission
  );

  const handleCommissionSelect = (value: number) => {
    setEditCommission(value.toString());
  };

  const searchBrokers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setSearchUsers([]);
      return;
    }

    setLoadingUsers(true);
    try {
      const response = await UserService.searchUsernames(searchTerm);
      // Mapear los datos al formato ProfileDto que necesitas
      const mappedUsers: ProfileDto[] = response.map((user: any) => ({
        id: user.id,
        username: user.username,
        roleName: user.roleName,
        avatarUrl: user.profileImage,
        userId: user.user?.id,
        profileImage: user.profileImage,
        active: user.active,
        profilePicture: user.profileImage,
        userProfileId: user.id,
        fullName: user.user?.fullName,
        roleId: user.roleId,
      }));

      const currentUserId = userToken?.user.profiles.find(
        (u) => u.roleId === 3
      )?.id;

      const users = mappedUsers.filter((u) => u.roleId === 3);

      const filteredUsers = users.filter((u) => u.id !== currentUserId);

      setSearchUsers(filteredUsers);
    } catch (error) {
      console.error("Error searching users:", error);
      setSearchUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddBrokerOpen = () => {
    setAddBrokerOpen(true);
    setSelectedBroker(null);
    setAddBrokerForm({
      requestName: "",
      description: "",
      commissionPercentaje: "",
    });
    setSearchUsers([]);
  };

  const handleAddBrokerClose = () => {
    setAddBrokerOpen(false);
    setSelectedBroker(null);
    setAddBrokerForm({
      requestName: "",
      description: "",
      commissionPercentaje: "",
    });
    setSearchUsers([]);
  };

  const handleAddBrokerFormChange = (field: string, value: string) => {
    setAddBrokerForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddBrokerCommissionSelect = (value: number) => {
    setAddBrokerForm((prev) => ({
      ...prev,
      commissionPercentaje: value.toString(),
    }));
  };

  const handleAddBrokerSave = async () => {
    if (
      !selectedBroker ||
      !addBrokerForm.requestName ||
      !addBrokerForm.commissionPercentaje
    ) {
      setMessage("Por favor completa todos los campos requeridos");
      setSnackbarError(true);
      return;
    }

    setSavingAddBroker(true);

    const brokerToSend = brokers.find(
      (b) => b.userProfileId === selectedBroker.id
    );

    console.log("brokers", brokers);

    console.log("brokerToSend", brokerToSend);

    try {
      const response = await createCollaborationRequest({
        propertyId: selectedProperty.id_Property,
        requestName: addBrokerForm.requestName,
        description: addBrokerForm.description,
        commissionPercentaje: parseFloat(addBrokerForm.commissionPercentaje),
        realtorId: brokerToSend?.id ?? 0,
      });

      if (response.success) {
        await fetchRequests();
        setMessage("Solicitud de colaboración enviada exitosamente");
        setSnackbarOpen(true);
        handleAddBrokerClose();
      }
    } catch (error: any) {
      setMessage(error.message || "Error al enviar la solicitud");
      setSnackbarError(true);
    } finally {
      setSavingAddBroker(false);
    }
  };

  return (
    <Box sx={{ mt: 4, px: { xs: 2, sm: 4 } }}>
      <Box sx={{ position: "relative", mb: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontWeight: 600, color: "#2e7d32", wordBreak: "break-word" }}
        >
          Solicitudes de colaboración
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddBrokerOpen}
          sx={{
            background: "linear-gradient(135deg, #66bb6a, #4caf50)",
            color: "white",
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: "bold",
            boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #5cb85c, #449d44)",
              boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
              transform: "translateY(-1px)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Añadir Broker
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mt: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Buscar solicitud..."
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              backgroundColor: "#f5f5f5",
              borderRadius: "100px",
              width: 250,
              "& .MuiOutlinedInput-root": {
                borderRadius: "100px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#757575" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{ width: 140, height: 140, ml: "auto", mt: { xs: 2, sm: 0 } }}
          >
            <Typography
              variant="h6"
              gutterBottom
              style={{
                textAlign: "center",
                color: "#2e7d32",
                fontSize: "10px",
              }}
            >
              Comisión total
            </Typography>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  dataKey="value"
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value: number) => `${value}%`} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress size={50} thickness={4} />
        </Box>
      ) : requests.length === 0 ? (
        <Typography align="center" sx={{ mt: 4, color: "#999" }}>
          No hay solicitudes disponibles.
        </Typography>
      ) : filteredRequests.length === 0 ? (
        <Typography align="center" sx={{ mt: 4, color: "#999" }}>
          No hay solicitudes encontradas.
        </Typography>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{
              display: { xs: "none", sm: "block" },
              overflowX: "auto",
              maxWidth: "100%",
              mb: 2,
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Propiedad</TableCell>
                  <TableCell>% Comisión</TableCell>
                  <TableCell>Pagada</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Broker</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRequests.map((req) => (
                  <TableRow
                    key={req.id}
                    onClick={() => handleOpenDetails(req)}
                    sx={{ cursor: "pointer" }}
                    title="Ver más detalles..."
                  >
                    <TableCell>{req.propertyListing?.title}</TableCell>
                    <TableCell>{req.commissionPercentaje}%</TableCell>
                    <TableCell>{req.isPaid ? "Si" : "No"}</TableCell>
                    <TableCell>{getStatusChip(req.status ?? 0)}</TableCell>
                    <TableCell>{req.contact?.fullName}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Aceptar solicitud">
                        <IconButton
                          onClick={(e) =>
                            handleAction("accept", req.id as number, e)
                          }
                          color="success"
                          disabled={req.status === 1 || req.status === 2}
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rechazar solicitud">
                        <IconButton
                          onClick={(e) =>
                            handleAction("reject", req.id as number, e)
                          }
                          color="error"
                          disabled={req.status === 1 || req.status === 2}
                        >
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                      {req.status === 0 && (
                        <Tooltip title="Editar solicitud">
                          <IconButton
                            onClick={() => handleEditOpen(req)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              flexDirection: "column",
              gap: 2,
            }}
          >
            {paginatedRequests.map((req) => {
              const { label, color, icon } = getStatusCardHeader(
                req.status ?? 0
              );
              return (
                <Paper
                  key={req.id}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: 3,
                    cursor: "pointer",
                    transition: "all 0.3s ease-in-out",
                    transform: "translateY(0)",
                    "&:hover": {
                      transform: "translateY(-5px) scale(1.02)",
                      boxShadow: 6,
                    },
                  }}
                  onClick={() => handleOpenDetails(req)}
                >
                  <Box
                    sx={{
                      backgroundColor: color,
                      color: "white",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {icon}
                      <Typography fontWeight="bold">{label}</Typography>
                    </Box>
                    <Typography variant="body2">
                      {req.propertyListing?.title}
                    </Typography>
                  </Box>

                  {/* Contenido */}
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ mb: 1 }}>
                      <strong>Broker:</strong> {req.contact?.fullName}
                    </Typography>
                    <Typography>
                      <strong>Comisión:</strong> {req.commissionPercentaje}%
                    </Typography>
                    <Typography>
                      <strong>Pagada:</strong> {req.isPaid ? "Sí" : "No"}
                    </Typography>

                    <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                      <Tooltip title="Aceptar solicitud">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAction("accept", req.id as number, e);
                          }}
                          color="success"
                          disabled={req.status === 1 || req.status === 2}
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rechazar solicitud">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAction("reject", req.id as number, e);
                          }}
                          color="error"
                          disabled={req.status === 1 || req.status === 2}
                        >
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar solicitud">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditOpen(req);
                          }}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Pagination
              count={Math.ceil(filteredRequests.length / itemsPerPage)}
              page={page}
              onChange={(_, value) => setPage(value)}
              shape="circular"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "50%",
                  color: "#4caf50",
                  "&.Mui-selected": {
                    backgroundColor: "#4caf50",
                    color: "white",
                  },
                },
              }}
            />
          </Box>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Confirmar acción</DialogTitle>
        <DialogContent>
          ¿Estás seguro de que deseas{" "}
          {selectedAction === "accept" ? "aceptar" : "rechazar"} esta solicitud?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
            }}
            variant="outlined"
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmAction}
            variant="contained"
            color={selectedAction === "accept" ? "success" : "error"}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={detailsOpen} onClose={() => setDetailsOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 3,
            p: 3,
            width: { xs: "95%", md: "80%" },
            maxWidth: 500,
            height: "90%",
            overflow: "auto",
          }}
        >
          <Stack spacing={2}>
            {/* Encabezado */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Info sx={{ color: "primary.main" }} />
                <Typography variant="h6" fontWeight={600}>
                  Detalles de la solicitud
                </Typography>
              </Box>

              <IconButton
                onClick={() => setDetailsOpen(false)}
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                    color: "#333",
                  },
                }}
              >
                <Close />
              </IconButton>
            </Box>

            <Divider />

            {/* Contenido */}
            {detailsRequest && (
              <>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 1 }}
                  >
                    Información de la propiedad
                  </Typography>
                  <Stack spacing={1}>
                    <Typography>
                      <strong>Título:</strong>{" "}
                      {detailsRequest.propertyListing.title}
                    </Typography>
                    <Typography>
                      <strong>Dirección:</strong>{" "}
                      {detailsRequest.propertyListing.address_Description}
                    </Typography>
                    <Typography>
                      <strong>Descripción:</strong> {detailsRequest.description}
                    </Typography>
                    <Typography>
                      <strong>Comisión:</strong>{" "}
                      {detailsRequest.commissionPercentaje}%
                    </Typography>
                    <Typography>
                      <strong>Pagada:</strong>{" "}
                      {detailsRequest.isPaid ? "Sí" : "No"}
                    </Typography>
                    <Typography>
                      <strong>Estado:</strong>{" "}
                      {getStatusChip(detailsRequest.status)}
                    </Typography>
                  </Stack>
                </Paper>

                <Paper
                  variant="outlined"
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 1 }}
                  >
                    Información del broker
                  </Typography>
                  <Stack spacing={1}>
                    <Typography>
                      <strong>Nombre:</strong> {detailsRequest.contact.fullName}
                    </Typography>
                    <Typography>
                      <strong>Email:</strong> {detailsRequest.contact.email}
                    </Typography>
                    <Typography>
                      <strong>Teléfono:</strong> {detailsRequest.contact.phone}
                    </Typography>
                    <Typography>
                      <strong>Dirección:</strong>{" "}
                      {detailsRequest.contact.address}
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    sx={{
                      borderRadius: 20,
                      textTransform: "none",
                      fontSize: "0.75rem",
                    }}
                    onClick={() =>
                      navigate(
                        `/properties/realtor/${detailsRequest.realtorId}`
                      )
                    }
                  >
                    Ver perfil
                  </Button>
                </Paper>
              </>
            )}
          </Stack>
        </Box>
      </Modal>

      <Snackbar
        open={snackBarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbarError}
        autoHideDuration={4000}
        onClose={() => setSnackbarError(false)}
      >
        <Alert
          onClose={() => setSnackbarError(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
      <Modal open={editOpen} onClose={() => setEditOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 3,
            p: 4,
            minWidth: 300,
            maxWidth: 400,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Editar solicitud
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Selección de comisión con chips */}
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, color: "#2e7d32", fontWeight: "medium" }}
          >
            Comisión:
            {commissionOptions.length === 1 && (
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                (Solo una opción disponible)
              </Typography>
            )}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {commissionOptions.map((value) => (
              <Chip
                key={value}
                icon={<Percent />}
                label={`${value}%`}
                clickable
                onClick={() => handleCommissionSelect(value)}
                sx={{
                  height: 40,
                  backgroundColor:
                    parseInt(editCommission) === value
                      ? "transparent"
                      : "rgba(76, 175, 80, 0.08)",
                  background:
                    parseInt(editCommission) === value
                      ? "linear-gradient(135deg, #66bb6a, #4caf50)"
                      : "rgba(76, 175, 80, 0.08)",
                  color:
                    parseInt(editCommission) === value ? "white" : "#2e7d32",
                  border:
                    parseInt(editCommission) === value
                      ? "none"
                      : "1px solid #81c784",
                  fontWeight:
                    parseInt(editCommission) === value ? "bold" : "medium",
                  fontSize: "0.9rem",
                  px: 1.5,
                  boxShadow:
                    parseInt(editCommission) === value
                      ? "0 4px 12px rgba(76, 175, 80, 0.3)"
                      : "none",
                  transition: "all 0.3s ease",
                  "& .MuiChip-icon": {
                    color:
                      parseInt(editCommission) === value ? "white" : "#4caf50",
                    fontSize: "18px",
                  },
                  "&:hover": {
                    transform: "translateY(-1px)",
                    background:
                      parseInt(editCommission) === value
                        ? "linear-gradient(135deg, #5cb85c, #449d44)"
                        : "rgba(76, 175, 80, 0.15)",
                    boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)",
                  },
                }}
              />
            ))}
          </Box>

          {/* Información sobre los términos de comisión */}
          {selectedProperty?.commission && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 2,
                color: "text.secondary",
                fontStyle: "italic",
                textAlign: "center",
              }}
            >
              💡 Opciones basadas en los términos: "
              {selectedProperty.commission}"
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Typography sx={{ mr: 1 }}>Pagado:</Typography>
            <Switch
              checked={editIsPaid}
              onChange={(e) => setEditIsPaid(e.target.checked)}
              color="success"
            />
          </Box>

          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={handleEditSave}
            disabled={!hasChanges() || savingEdit}
            sx={{
              borderRadius: 2,
              py: 1.2,
              background: "linear-gradient(135deg, #66bb6a, #4caf50)",
              "&:hover": {
                background: "linear-gradient(135deg, #5cb85c, #449d44)",
              },
            }}
          >
            {savingEdit ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Guardar cambios"
            )}
          </Button>
        </Box>
      </Modal>
      <Modal open={addBrokerOpen} onClose={handleAddBrokerClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: { xs: 2, sm: 3 },
            p: { xs: 2, sm: 4 },
            width: { xs: "95vw", sm: "90vw", md: 500 },
            maxWidth: { xs: 400, sm: 500 },
            maxHeight: { xs: "95vh", sm: "90vh" },
            overflow: "auto",
          }}
        >
          {/* Header con botón de cerrar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#2e7d32",
                fontWeight: "bold",
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              Añadir Broker para Colaboración
            </Typography>

            <IconButton
              onClick={handleAddBrokerClose}
              sx={{
                color: "#666",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  color: "#333",
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>

          <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

          {/* Seleccionar Broker */}
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              color: "#2e7d32",
              fontWeight: "medium",
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
            }}
          >
            Seleccionar Broker *
          </Typography>
          <Autocomplete
            options={searchUsers}
            getOptionLabel={(option) =>
              option.fullName || option.username || ""
            }
            value={selectedBroker}
            onChange={(_, newValue) => setSelectedBroker(newValue)}
            onInputChange={(_, newInputValue) => {
              searchBrokers(newInputValue);
            }}
            loading={loadingUsers}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Buscar broker por nombre..."
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <Person
                        sx={{
                          color: "#4caf50",
                          mr: 1,
                          fontSize: { xs: 20, sm: 24 },
                        }}
                      />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <ListItem {...props} key={option.id} dense>
                <ListItemAvatar>
                  <Avatar
                    src={option.profileImage || option.avatarUrl}
                    sx={{
                      width: { xs: 28, sm: 32 },
                      height: { xs: 28, sm: 32 },
                    }}
                  >
                    {option.fullName?.charAt(0) || option.username?.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                    >
                      {option.fullName || option.username}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      @{option.username}
                    </Typography>
                  }
                />
              </ListItem>
            )}
            noOptionsText="No se encontraron brokers"
            loadingText="Buscando..."
          />

          {/* Nombre de la solicitud */}
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              color: "#2e7d32",
              fontWeight: "medium",
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
            }}
          >
            Nombre de la solicitud *
          </Typography>
          <TextField
            fullWidth
            placeholder="Ej: Colaboración Casa en Escazú"
            value={addBrokerForm.requestName}
            onChange={(e) =>
              handleAddBrokerFormChange("requestName", e.target.value)
            }
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />

          {/* Descripción */}
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              color: "#2e7d32",
              fontWeight: "medium",
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
            }}
          >
            Descripción
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Describe los detalles de la colaboración..."
            value={addBrokerForm.description}
            onChange={(e) =>
              handleAddBrokerFormChange("description", e.target.value)
            }
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />

          {/* Selección de comisión con chips */}
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              color: "#2e7d32",
              fontWeight: "medium",
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
            }}
          >
            Porcentaje de comisión *
            {commissionOptions.length === 1 && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  ml: 1,
                  color: "text.secondary",
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                }}
              >
                (Solo una opción disponible)
              </Typography>
            )}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, sm: 2 },
              mb: 2,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {commissionOptions.map((value) => (
              <Chip
                key={value}
                icon={<Percent />}
                label={`${value}%`}
                clickable
                onClick={() => handleAddBrokerCommissionSelect(value)}
                sx={{
                  height: { xs: 36, sm: 40 },
                  backgroundColor:
                    parseInt(addBrokerForm.commissionPercentaje) === value
                      ? "transparent"
                      : "rgba(76, 175, 80, 0.08)",
                  background:
                    parseInt(addBrokerForm.commissionPercentaje) === value
                      ? "linear-gradient(135deg, #66bb6a, #4caf50)"
                      : "rgba(76, 175, 80, 0.08)",
                  color:
                    parseInt(addBrokerForm.commissionPercentaje) === value
                      ? "white"
                      : "#2e7d32",
                  border:
                    parseInt(addBrokerForm.commissionPercentaje) === value
                      ? "none"
                      : "1px solid #81c784",
                  fontWeight:
                    parseInt(addBrokerForm.commissionPercentaje) === value
                      ? "bold"
                      : "medium",
                  fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  px: { xs: 1, sm: 1.5 },
                  boxShadow:
                    parseInt(addBrokerForm.commissionPercentaje) === value
                      ? "0 4px 12px rgba(76, 175, 80, 0.3)"
                      : "none",
                  transition: "all 0.3s ease",
                  "& .MuiChip-icon": {
                    color:
                      parseInt(addBrokerForm.commissionPercentaje) === value
                        ? "white"
                        : "#4caf50",
                    fontSize: { xs: "16px", sm: "18px" },
                  },
                  "&:hover": {
                    transform: "translateY(-1px)",
                    background:
                      parseInt(addBrokerForm.commissionPercentaje) === value
                        ? "linear-gradient(135deg, #5cb85c, #449d44)"
                        : "rgba(76, 175, 80, 0.15)",
                    boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)",
                  },
                }}
              />
            ))}
          </Box>

          {/* Información sobre los términos de comisión */}
          {selectedProperty?.commission && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: { xs: 2, sm: 3 },
                color: "text.secondary",
                fontStyle: "italic",
                textAlign: "center",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                px: { xs: 1, sm: 0 },
              }}
            >
              💡 Opciones basadas en los términos: "
              {selectedProperty.commission}"
            </Typography>
          )}

          {/* Botones */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant="outlined"
              onClick={handleAddBrokerClose}
              sx={{
                flex: 1,
                borderColor: "#81c784",
                color: "#2e7d32",
                py: { xs: 1.2, sm: 1 },
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                order: { xs: 2, sm: 1 },
                "&:hover": {
                  borderColor: "#4caf50",
                  backgroundColor: "rgba(76, 175, 80, 0.08)",
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleAddBrokerSave}
              disabled={
                !selectedBroker ||
                !addBrokerForm.requestName ||
                !addBrokerForm.commissionPercentaje ||
                savingAddBroker
              }
              sx={{
                flex: 1,
                py: { xs: 1.2, sm: 1 },
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                order: { xs: 1, sm: 2 },
                background: "linear-gradient(135deg, #66bb6a, #4caf50)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5cb85c, #449d44)",
                },
                "&:disabled": {
                  background: "rgba(76, 175, 80, 0.3)",
                  color: "rgba(255, 255, 255, 0.6)",
                },
              }}
            >
              {savingAddBroker ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Enviar Solicitud"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
